import {
  assertArray,
  assertNonEmptyString,
  assertRecord,
  hasOwn,
  inspectNested,
  rejectUnknownKeys,
  validateEnum,
  validateOptionalString,
} from '../elements/elementFields.validation';
import { validateRankedSpatialRecordIdentity } from '../elements/mapFields.validation';
import type { FmElementType } from '../elements/elements.types';
import { validateElement } from '../elements/elements.validation';
import { rejectRepeatedSectionCopy } from './sectionCopyEcho';
import { validateState } from './sectionState.validation';
import {
  FM_BAND_IMAGE_FITS,
  FM_BAND_SCRIMS,
  FM_GRID_COLUMNS,
  FM_SECTION_DENSITIES,
  FM_SECTION_HEADLINES,
  FM_SECTION_ARRANGEMENTS,
  FM_SECTION_INTENTS,
  FM_SECTION_PANEL_ELEMENTS,
  FM_SECTION_SLOTS,
  FM_SECTION_TONES,
  FM_SECTION_WIDTHS,
  FM_SPLIT_BALANCES,
  FM_SPLIT_SIDES,
  FM_SPLIT_STACK_ORDERS,
  type FmSection,
  type FmSectionArrangement,
} from './section.types';

type SectionRecord = Record<string, unknown>;

const COMMON_KEYS = [
  'id',
  'arrangement',
  'state',
  'tone',
  'width',
  'density',
  'intent',
  'testId',
  'notices',
];
const ARRANGEMENT_KEYS: Record<FmSectionArrangement, readonly string[]> = {
  prose: ['headline'],
  split: ['side', 'balance', 'stackOrder', 'sticky'],
  figures: [],
  grid: ['columns'],
  tabbed: ['label', 'panels'],
  carousel: ['labels', 'slides'],
  band: ['scrim', 'imageFit'],
};
const THEME_KEYS = ['data-theme', 'dataTheme']; // design-rescue-ok: refused keys
const CAROUSEL_LABEL_KEYS = ['carousel', 'previous', 'next', 'slide'];
const PANEL_FIELD = { tabbed: 'panels', carousel: 'slides' } as const;
const READY_STATUSES = new Set(['ready', 'partial', 'stale']);
const SWITCHER_ARRANGEMENTS = new Set<FmSectionArrangement>(['tabbed', 'carousel']);

function invalid(path: string, message: string): never {
  throw new Error(`Invalid descriptor: ${path} ${message}.`);
}

function isArrangement(value: unknown): value is FmSectionArrangement {
  return (
    typeof value === 'string' && (FM_SECTION_ARRANGEMENTS as readonly string[]).includes(value)
  );
}

/** Tone flows through the section band; a component-local theme anywhere is an escape. */
function rejectThemeEscape(value: unknown, path: string): void {
  if (!value || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach((item, index) => rejectThemeEscape(item, `${path}[${index}]`));
    return;
  }
  Object.entries(value as SectionRecord).forEach(([key, entry]) => {
    if (THEME_KEYS.includes(key)) {
      invalid(`${path}.${key}`, 'is forbidden; section tone sets the band theme');
    }
    rejectThemeEscape(entry, `${path}.${key}`);
  });
}

function validateElementList(
  value: unknown,
  allowed: readonly FmElementType[],
  path: string,
): SectionRecord[] {
  assertArray(value, path);
  if (value.length === 0) invalid(path, 'must not be empty');
  value.forEach((element, index) => {
    const elementPath = `${path}[${index}]`;
    validateElement(element, elementPath);
    if (!allowed.includes(element.element)) {
      invalid(
        `${elementPath}.element`,
        `"${element.element}" is not allowed here; use ${allowed.join(', ')}`,
      );
    }
  });
  return value as SectionRecord[];
}

function validateSlots(
  section: SectionRecord,
  arrangement: FmSectionArrangement,
  path: string,
): SectionRecord[] {
  const specs: Record<string, { required: boolean; elements: readonly FmElementType[] }> =
    FM_SECTION_SLOTS[arrangement];
  const slotsPath = `${path}.slots`;
  if (!hasOwn(section, 'slots'))
    invalid(slotsPath, 'is required in a ready, partial or stale state');
  assertRecord(section.slots, slotsPath);
  const slots = section.slots;
  rejectUnknownKeys(slots, Object.keys(specs), slotsPath);
  const elements: SectionRecord[] = [];
  Object.entries(specs).forEach(([slot, spec]) => {
    if (!hasOwn(slots, slot)) {
      if (spec.required)
        invalid(`${slotsPath}.${slot}`, `is required by arrangement "${arrangement}"`);
      return;
    }
    elements.push(...validateElementList(slots[slot], spec.elements, `${slotsPath}.${slot}`));
  });
  if (arrangement === 'grid' && Array.isArray(slots.items) && slots.items.length < 2) {
    invalid(`${slotsPath}.items`, 'needs at least two elements; one element is a prose section');
  }
  return elements;
}

/**
 * Tabs switch between panels of different element types. Panels that each hold
 * one element of the same type are a lens over one element (KTD11).
 */
function rejectSameTypeTabs(panels: SectionRecord[], path: string): void {
  const signatures = panels.map((panel) =>
    (panel.elements as SectionRecord[]).map((element) => element.element).join('+'),
  );
  const single = panels.every((panel) => (panel.elements as unknown[]).length === 1);
  if (single && new Set(signatures).size === 1) {
    invalid(path, `all hold one "${signatures[0]}" element; switch payloads with a lens instead`);
  }
}

function validatePanels(
  section: SectionRecord,
  arrangement: 'tabbed' | 'carousel',
  path: string,
): SectionRecord[] {
  const field = PANEL_FIELD[arrangement];
  const panelsPath = `${path}.${field}`;
  assertArray(section[field], panelsPath);
  const panels = section[field] as unknown[];
  if (panels.length < 2) invalid(panelsPath, 'needs at least two entries');
  const keys = new Set<string>();
  const elements: SectionRecord[] = [];
  panels.forEach((panel, index) => {
    const panelPath = `${panelsPath}[${index}]`;
    assertRecord(panel, panelPath);
    rejectUnknownKeys(panel, ['key', 'label', 'testId', 'elements'], panelPath);
    assertNonEmptyString(panel.key, `${panelPath}.key`);
    validateOptionalString(panel, 'testId', panelPath);
    assertNonEmptyString(panel.label, `${panelPath}.label`);
    if (keys.has(panel.key)) invalid(`${panelPath}.key`, `duplicates "${panel.key}"`);
    keys.add(panel.key);
    elements.push(
      ...validateElementList(
        panel.elements,
        FM_SECTION_PANEL_ELEMENTS[arrangement],
        `${panelPath}.elements`,
      ),
    );
  });
  if (arrangement === 'tabbed') rejectSameTypeTabs(panels as SectionRecord[], panelsPath);
  return elements;
}

function hasMeaningfulValue(elements: readonly SectionRecord[]): boolean {
  return elements.some((element) => {
    if (element.element === 'keyFigures') {
      return (element.figures as SectionRecord[]).some(
        (figure) => typeof figure.value === 'string',
      );
    }
    if (element.element === 'facts') {
      return (element.items as SectionRecord[]).some((item) => typeof item.value === 'string');
    }
    return false;
  });
}

/** Composition rules that used to live in per-kind checks. */
function validateComposition(
  section: SectionRecord,
  elements: readonly SectionRecord[],
  path: string,
): void {
  const primaryCount = elements.filter(
    (element) => element.element === 'actions' && hasOwn(element, 'primary'),
  ).length;
  if (primaryCount > 1) {
    invalid(path, `carries ${primaryCount} primary actions; a section carries at most one`);
  }
  if (section.intent === 'decision' && primaryCount !== 1) {
    invalid(path, 'is a decision section and needs exactly one primary action');
  }
  if (section.intent === 'value' && !hasMeaningfulValue(elements)) {
    invalid(path, 'is a value section and needs an available key figure or a fact with a value');
  }
  validateRankedSpatialRecordIdentity(elements, path);
}

function validateArrangementOptions(section: SectionRecord, path: string): void {
  if (section.arrangement === 'split') {
    validateEnum(section.side, FM_SPLIT_SIDES, `${path}.side`, true);
    validateEnum(section.balance, FM_SPLIT_BALANCES, `${path}.balance`, true);
    validateEnum(section.stackOrder, FM_SPLIT_STACK_ORDERS, `${path}.stackOrder`, true);
    if (hasOwn(section, 'sticky') && typeof section.sticky !== 'boolean') {
      invalid(`${path}.sticky`, 'must be a boolean');
    }
    if (section.sticky === true && section.width === 'full') {
      invalid(`${path}.width`, 'is "full" on a sticky split; a pinned column stays on the rail');
    }
  }
  if (section.arrangement === 'grid') {
    validateEnum(section.columns, FM_GRID_COLUMNS, `${path}.columns`);
  }
  if (section.arrangement === 'prose') {
    validateEnum(section.headline, FM_SECTION_HEADLINES, `${path}.headline`, true);
  }
  if (section.arrangement === 'band') validateBandOptions(section, path);
}

/** A band image needs a background to scrim; an illustration is never scrimmed. */
function validateBandOptions(section: SectionRecord, path: string): void {
  validateEnum(section.scrim, FM_BAND_SCRIMS, `${path}.scrim`, true);
  validateEnum(section.imageFit, FM_BAND_IMAGE_FITS, `${path}.imageFit`, true);
  if (section.imageFit === 'contain' && hasOwn(section, 'scrim')) {
    invalid(`${path}.scrim`, 'is refused with imageFit "contain"; an illustration sits beside the copy unscrimmed');
  }
  const slots = section.slots as SectionRecord | undefined;
  const hasBackground = Boolean(slots && hasOwn(slots, 'background'));
  const option = ['scrim', 'imageFit'].find((key) => hasOwn(section, key));
  if (option && READY_STATUSES.has((section.state as SectionRecord | undefined)?.status as string) && !hasBackground) {
    invalid(`${path}.${option}`, 'needs a background image in slots.background');
  }
}

/**
 * The editorial split places the lead copy's headline beside its body, so the
 * first content element must be a copy with a heading and something to read.
 */
function validateEditorialLead(section: SectionRecord, path: string): void {
  if (section.arrangement !== 'prose' || section.headline !== 'beside') return;
  const lead = ((section.slots as SectionRecord).content as SectionRecord[])[0];
  if (lead.element !== 'copy' || !hasOwn(lead, 'heading')) {
    invalid(`${path}.slots.content[0]`, 'must be a copy with a heading when headline is "beside"');
  }
  const content = (section.slots as SectionRecord).content as SectionRecord[];
  const hasBody =
    ['lede', 'body', 'list'].some((key) => lead[key] !== undefined) || content.length > 1;
  if (!hasBody) {
    invalid(`${path}.slots.content`, 'has only a headline; "beside" needs a body to sit beside');
  }
}

function validatePanelNames(section: SectionRecord, path: string): void {
  if (section.arrangement === 'tabbed') assertNonEmptyString(section.label, `${path}.label`);
  if (section.arrangement !== 'carousel') return;
  const labelsPath = `${path}.labels`;
  const labels = section.labels;
  assertRecord(labels, labelsPath);
  rejectUnknownKeys(labels, CAROUSEL_LABEL_KEYS, labelsPath);
  CAROUSEL_LABEL_KEYS.forEach((key) => assertNonEmptyString(labels[key], `${labelsPath}.${key}`));
  if (!String(labels.slide).includes('{index}')) {
    invalid(`${labelsPath}.slide`, 'must carry the {index} placeholder');
  }
}

function validateReadyContent(
  section: SectionRecord,
  arrangement: FmSectionArrangement,
  path: string,
): void {
  const elements = validateSlots(section, arrangement, path);
  validateEditorialLead(section, path);
  if (arrangement === 'tabbed' || arrangement === 'carousel') {
    validatePanelNames(section, path);
    elements.push(...validatePanels(section, arrangement, path));
  }
  validateComposition(section, elements, path);
  rejectRepeatedSectionCopy(section, path);
}

/**
 * Validates one section at `path`. Every refusal names the offending path:
 * an unknown arrangement or key, `layout`, a theme escape, numeric section
 * geometry (every section geometry field is an enum), a missing required slot,
 * an element a slot does not accept, or a broken composition rule. Element
 * payloads keep their own fields: `actions.set` still carries the legacy
 * action-set columns, media side and auto-cycle until the element contract
 * narrows it.
 */
export function validateSection(value: unknown, path = 'section'): asserts value is FmSection {
  assertRecord(value, path);
  const section = value;
  if (hasOwn(section, 'layout')) {
    invalid(`${path}.layout`, 'is not a section key; name the arrangement with "arrangement"');
  }
  rejectThemeEscape(section, path);
  if (!isArrangement(section.arrangement)) {
    invalid(`${path}.arrangement`, `must be one of ${FM_SECTION_ARRANGEMENTS.join(', ')}`);
  }
  const arrangement = section.arrangement;
  rejectUnknownKeys(
    section,
    [...COMMON_KEYS, ...ARRANGEMENT_KEYS[arrangement], 'slots', 'lead'],
    path,
  );
  assertNonEmptyString(section.id, `${path}.id`);
  validateOptionalString(section, 'testId', path);
  validateEnum(section.tone, FM_SECTION_TONES, `${path}.tone`, true);
  validateEnum(section.width, FM_SECTION_WIDTHS, `${path}.width`, true);
  validateEnum(section.density, FM_SECTION_DENSITIES, `${path}.density`, true);
  validateEnum(section.intent, FM_SECTION_INTENTS, `${path}.intent`, true);
  validateArrangementOptions(section, path);
  if (hasOwn(section, 'notices'))
    validateElementList(section.notices, ['notice'], `${path}.notices`);
  validateState(section.state, path);
  inspectNested(section.state, `${path}.state`);
  const contentKeys =
    arrangement === 'tabbed' || arrangement === 'carousel'
      ? ['slots', PANEL_FIELD[arrangement], ...ARRANGEMENT_KEYS[arrangement]]
      : ['slots'];
  if (READY_STATUSES.has((section.state as SectionRecord).status as string)) {
    if (hasOwn(section, 'lead')) {
      invalid(`${path}.lead`, 'belongs in the slots while the section shows content');
    }
    validateReadyContent(section, arrangement, path);
    return;
  }
  if (hasOwn(section, 'lead')) validateElementList([section.lead], ['copy'], `${path}.lead`);
  const leaked = contentKeys.find((key) => hasOwn(section, key));
  if (leaked) {
    invalid(
      `${path}.${leaked}`,
      `cannot be carried in state "${String((section.state as SectionRecord).status)}"`,
    );
  }
}

/**
 * Validates the sections of one chapter: unique ids, and at most one tabbed
 * or carousel section so a chapter never stacks two switchers.
 */
export function validateSections(
  values: readonly unknown[],
  path = 'sections',
): asserts values is readonly FmSection[] {
  const ids = new Set<string>();
  let switcher: string | undefined;
  values.forEach((value, index) => {
    const sectionPath = `${path}[${index}]`;
    validateSection(value, sectionPath);
    if (ids.has(value.id)) invalid(`${sectionPath}.id`, `duplicates "${value.id}"`);
    ids.add(value.id);
    if (!SWITCHER_ARRANGEMENTS.has(value.arrangement)) return;
    if (switcher) {
      invalid(
        sectionPath,
        `is a second tabbed or carousel section in the chapter (first: ${switcher})`,
      );
    }
    switcher = sectionPath;
  });
}
